import { useAbility } from "@casl/react";
import { useQuery } from "@tanstack/react-query";

import type { AppAbility } from "@recobra/core";
import type { Task } from "@recobra/schemas/task";

import { tasksApi } from "../../lib/api";
import { resolveTaskExecutionState, taskContextRoute } from "../task-policy";

/**
 * Reads the single-task record behind the modal.
 *
 * `placeholderTask` is a list row rendered while that read is in flight. It is
 * deliberately never written to the query cache: list responses omit the fields
 * only the single-task read computes (`previousActivities`), so caching one would
 * satisfy `staleTime` and suppress the read that produces them.
 */
export const useTaskModalData = ({
  taskId,
  placeholderTask,
}: {
  taskId: string | null;
  placeholderTask: Task | undefined;
}) => {
  const ability = useAbility<AppAbility>();
  const { data: task, error } = useQuery({
    queryKey: ["tasks", taskId],
    queryFn: () => tasksApi.getTask(taskId ?? ""),
    enabled: !!taskId,
    placeholderData: placeholderTask?.id === taskId ? placeholderTask : undefined,
  });
  return {
    task,
    error,
    executionState: resolveTaskExecutionState(task, ability),
    contextRoute: taskContextRoute(task),
  };
};
